import React from 'react';
import PropTypes from 'prop-types';
import { Menu, Tooltip } from 'antd';
import { ClipboardText, FolderSimplePlus, ListChecks } from '@phosphor-icons/react';
import COLORS from '../../util/constants/colors';
import { captureAnalytics } from '../../actions/preload';
import './chartActionsMenu.scss';

// ENG-3012: a disabled item explains itself through antd's Tooltip. Text placed
// inside the item is clipped -- `.ant-menu-vertical .ant-menu-item` is
// `height: 40px; overflow: hidden` (antd.css) -- so the reason was invisible.
// Applied to EVERY disabled item, not just Send to Chart: an all-TIFF reply 422s
// on all three actions, so all three need the reason.
const withDisabledReason = (item, { disabled, reason }) => (
  disabled && reason ? <Tooltip title={reason} placement="left">{item}</Tooltip> : item
);

const selectChartAction = (handler, action, variant) => (event) => {
  if (variant === 'described') captureAnalytics('record_viewer_chart_action_selected', { action });
  handler(event);
};

const ChartActionsMenu = ({
  canSendToChart,
  canCreateChart,
  canCreateTask,
  disableSendToChart,
  disableCreateChart,
  disableCreateTask,
  disabledExplanation,
  onSendToChart,
  onCreateChart,
  onCreateTask,
  sendLabel,
  createChartLabel,
  createTaskLabel,
  variant,
}) => (
  <Menu className={`chart-actions-menu${variant === 'described' ? ' chart-actions-menu--described' : ''}`} selectable={false}>
    {canSendToChart && (
      <Menu.Item
        key="send-to-chart"
        onClick={selectChartAction(onSendToChart, 'send_to_chart', variant)}
        disabled={disableSendToChart}
        // Stable hook for the "Send a document to a chart" guided tutorial: this
        // click is the action the tutorial teaches, so it also ends it.
        data-tutorial="chart-send-to-chart"
      >
        <span className="dropdown-icon"><ClipboardText size={20} weight="fill" color={COLORS.PURPLE_PRIMARY_LITE} /></span>
        {withDisabledReason(
          <span className="chart-menu-item-text">
            <span>{sendLabel}</span>
            {variant === 'described' && <span className="chart-menu-item-description">Files this document in an existing patient’s chart.</span>}
          </span>,
          { disabled: disableSendToChart, reason: disabledExplanation },
        )}
      </Menu.Item>
    )}
    {canCreateChart && (
      <Menu.Item
        key="create-chart"
        onClick={selectChartAction(onCreateChart, 'create_chart', variant)}
        disabled={disableCreateChart}
        // Either chart action satisfies the tutorial, so this one is a completing
        // click too (see TUTORIAL_COMPLETING_CLICKS in tutorialSteps).
        data-tutorial="chart-create-chart"
      >
        <span className="dropdown-icon"><FolderSimplePlus size={20} color={COLORS.PURPLE_PRIMARY_LITE} /></span>
        {withDisabledReason(
          <span className="chart-menu-item-text">
            <span>{createChartLabel}</span>
            {variant === 'described' && <span className="chart-menu-item-description">Creates a new patient chart and files this document.</span>}
          </span>,
          { disabled: disableCreateChart, reason: disabledExplanation },
        )}
      </Menu.Item>
    )}
    {canCreateTask && (
      <Menu.Item
        key="create-task"
        onClick={selectChartAction(onCreateTask, 'create_task', variant)}
        disabled={disableCreateTask}
      >
        <span className="dropdown-icon"><ListChecks size={20} color={COLORS.PURPLE_PRIMARY_LITE} /></span>
        {withDisabledReason(
          <span className="chart-menu-item-text">
            <span>{createTaskLabel}</span>
            {variant === 'described' && (
              <span className="chart-menu-item-description">
                {createTaskLabel === 'Create Note'
                  ? 'Creates a note in your EMR.'
                  : 'Creates an EMR task without filing this document in the chart.'}
              </span>
            )}
          </span>,
          { disabled: disableCreateTask, reason: disabledExplanation },
        )}
      </Menu.Item>
    )}
  </Menu>
);

ChartActionsMenu.propTypes = {
  variant: PropTypes.oneOf(['compact', 'described']),
  canSendToChart: PropTypes.bool,
  canCreateChart: PropTypes.bool,
  canCreateTask: PropTypes.bool,
  disableSendToChart: PropTypes.bool,
  disableCreateChart: PropTypes.bool,
  disableCreateTask: PropTypes.bool,
  disabledExplanation: PropTypes.string,
  onSendToChart: PropTypes.func,
  onCreateChart: PropTypes.func,
  onCreateTask: PropTypes.func,
  sendLabel: PropTypes.string,
  createChartLabel: PropTypes.string,
  createTaskLabel: PropTypes.string,
};

ChartActionsMenu.defaultProps = {
  variant: 'compact',
  canSendToChart: false,
  canCreateChart: false,
  canCreateTask: false,
  disableSendToChart: false,
  disableCreateChart: false,
  disableCreateTask: false,
  disabledExplanation: undefined,
  onSendToChart: () => {},
  onCreateChart: () => {},
  onCreateTask: () => {},
  sendLabel: 'Send to Chart',
  createChartLabel: 'Create Chart',
  createTaskLabel: 'Create Task',
};

export default ChartActionsMenu;
